'use client';
/* Built In Imports */
import { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

/* External Imports */
import { Box, Image } from '@chakra-ui/react';

/**
 * CardGalleryImages Component
 * @param {Object} images - Array of image objects
 * @param {number} index - Current index of the slider (if needed for RTL)
 * @returns {JSX.Element}
 */

const CardGalleryImages = ({ images, index }) => {
  const sliderRef = useRef(null);
  const containerRef = useRef(null);
  const [grayScaleColor, setGrayScaleColor] = useState(0);
  const [isScrolling, setIsScrolling] = useState(true);
  const [scrollAnimation, setScrollAnimation] = useState('increasing');
  const intervalDuration = 12000;
  const intervalStep = 100;

  useEffect(() => {
    let intervalId;

    const handleGrayscaleTransition = () => {
      setGrayScaleColor(prev => {
        if (scrollAnimation === 'increasing') {
          if (prev >= 100) {
            setIsScrolling(false);
            setScrollAnimation('decreasing');
            setTimeout(() => setIsScrolling(true), 5000);
            return 100;
          }
          return Math.min(prev + 100 / (intervalDuration / intervalStep), 100);
        } else {
          if (prev <= 0) {
            setIsScrolling(false);
            setScrollAnimation('increasing');
            setTimeout(() => setIsScrolling(true), 5000);
            return 0;
          }
          return Math.max(prev - 100 / (intervalDuration / intervalStep), 0);
        }
      });
    };

    const updateTransition = () => {
      if (isScrolling) {
        handleGrayscaleTransition();
      }
    };

    intervalId = setInterval(updateTransition, intervalStep);

    return () => {
      clearInterval(intervalId);
    };
  }, [isScrolling, scrollAnimation]);

  useEffect(() => {
    const handleSliderPlayPause = () => {
      if (sliderRef.current) {
        if (isScrolling) {
          sliderRef.current.slickPlay();
        } else {
          sliderRef.current.slickPause();
        }
      }
    };
    handleSliderPlayPause();
  }, [isScrolling]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setIsScrolling(false);
        } else {
          setIsScrolling(true);
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0.5], // 50% of the component is visible
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const settings = {
    dots: false,
    infinite: true,
    slidesToShow: index % 2 === 0 ? 3.5 : 3,
    slidesToScroll: 1,
    initialSlide: index,
    vertical: true,
    pauseOnHover: false,
    verticalSwiping: true,
    autoplay: true,
    speed: 5000,
    autoplaySpeed: 0,
    cssEase: 'linear',
    rtl: index % 2 === 0,
  };

  return (
    <Box
      ref={containerRef}
      className="slider-container"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection={{ base: 'row', md: 'column' }}
      maxW={{ base: '100%', md: '50%', lg: '25%' }}
      height={{ base: '24.5rem', md: '50rem' }}
      width="100%"
      overflow="hidden"
      position="relative"
      flex={{ base: '100%', md: '50%', lg: '25%' }}
    >
      <Slider ref={sliderRef} {...settings}>
        {images.map((img, imgId) => (
          <Box
            key={imgId}
            cursor="default"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={img?.url}
              alt="Image"
              width={{ base: '100%', md: '300px' }}
              height={{ base: '350px', md: '400px' }}
              objectFit="cover"
              my={1.5}
            />
          </Box>
        ))}
      </Slider>
      <style>
        {`
          .slider-container {
            transition: filter 1s linear;
            filter: grayscale(${grayScaleColor}%);
          }
        `}
      </style>
    </Box>
  );
};

export default CardGalleryImages;
